import type { ReactNode } from "react";

/**
 * Sehr kleiner Markdown-Renderer (Ueberschriften, Absaetze, Listen, Tabellen,
 * fett, Code-Bloecke). Bewusst ohne zusätzliche Abhängigkeit - die Briefings
 * kommen aus unserem eigenen Prompt und nutzen genau diese Elemente.
 * Es wird KEIN HTML aus dem Text interpretiert (kein dangerouslySetInnerHTML).
 */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) out.push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else out.push(<code key={i++}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text }: { text: string }) {
  const lines = (text || "").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim().startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith("```")) buf.push(lines[i++]);
      i++;
      blocks.push(<pre key={key++} className="md-code">{buf.join("\n")}</pre>);
      continue;
    }
    if (/^#{1,4}\s/.test(line)) {
      const level = line.match(/^#+/)![0].length;
      const content = line.replace(/^#+\s*/, "");
      const H = (["h1", "h2", "h3", "h4"][level - 1] ?? "h4") as "h1";
      blocks.push(<H key={key++} className="md-h">{inline(content)}</H>);
      i++;
      continue;
    }
    if (/^\s*\|/.test(line) && i + 1 < lines.length && /^\s*\|[-\s|:]+\|\s*$/.test(lines[i + 1])) {
      const cells = (l: string) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i])) rows.push(cells(lines[i++]));
      blocks.push(
        <div key={key++} className="md-table-wrap">
          <table className="md-table">
            <thead><tr>{head.map((h, n) => <th key={n}>{inline(h)}</th>)}</tr></thead>
            <tbody>{rows.map((r, n) => <tr key={n}>{r.map((c, m2) => <td key={m2}>{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, ""));
        i++;
      }
      const L = ordered ? "ol" : "ul";
      blocks.push(<L key={key++} className="md-list">{items.map((it, n) => <li key={n}>{inline(it)}</li>)}</L>);
      continue;
    }
    if (/^\s*---+\s*$/.test(line)) { blocks.push(<hr key={key++} className="md-hr" />); i++; continue; }
    if (!line.trim()) { i++; continue; }

    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*[-*]\s|\s*\d+\.\s|\s*\||```|---)/.test(lines[i])) {
      para.push(lines[i++]);
    }
    if (para.length) {
      const txt = para.join(" ");
      const em = txt.startsWith("*") && txt.endsWith("*") && !txt.startsWith("**");
      blocks.push(
        <p key={key++} className={`md-p ${em ? "md-meta" : ""}`}>
          {inline(em ? txt.slice(1, -1) : txt)}
        </p>,
      );
    }
  }
  return <div className="md">{blocks}</div>;
}

export default Markdown;
